import { useQuery } from '@tanstack/react-query'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import { addMember, listUsers, updateMember, type GroupMember } from '../../../../types'
import { actionApiCall, formSubmit } from '../../api/actionApiCall'
import { CheckboxField, SelectField, SubmitButton, TextField, useFormState } from '../../components/form'

const BEZ_UCTU = ''

export type MemberFormModalProps = {
    show: boolean
    groupId: string
    /** Bez člena jde o přidání nového. */
    member?: GroupMember
    onClose: () => void
    onSaved: () => void
}

export function MemberFormModal({ show, groupId, member, onClose, onSaved }: MemberFormModalProps) {
    const isEdit = !!member

    const { data: users } = useQuery({
        queryKey: ['users'],
        queryFn: () => actionApiCall(() => listUsers(), { silent: true }),
    })

    const form = useFormState({
        initialValues: {
            displayName: member?.displayName ?? '',
            userId: member?.userId ?? BEZ_UCTU,
            isActive: member?.isActive ?? true,
        },
        onSubmit: async (values) => {
            const userId = values.userId === BEZ_UCTU ? null : values.userId

            const { data, fieldErrors } = isEdit
                ? await formSubmit(
                      () =>
                          updateMember({
                              path: { groupId, memberId: member.id },
                              body: { displayName: values.displayName, userId, isActive: values.isActive },
                          }),
                      { successMessage: 'Člen byl upraven.' },
                  )
                : await formSubmit(() => addMember({ path: { groupId }, body: { displayName: values.displayName, userId } }), {
                      successMessage: 'Člen byl přidán.',
                  })

            if (!data) {
                return { fieldErrors }
            }

            onSaved()
            onClose()
        },
    })

    const userOptions = [
        { value: BEZ_UCTU, label: 'Bez účtu (nepřihlašuje se)' },
        ...(users ?? []).map((user) => ({ value: user.id, label: `${user.displayName} (${user.login})` })),
    ]

    return (
        <Modal show={show} onHide={onClose} centered>
            <form onSubmit={form.handleSubmit} noValidate>
                <Modal.Header closeButton>
                    <Modal.Title as="h2" className="h5">{isEdit ? 'Úprava člena' : 'Nový člen skupiny'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <TextField
                        label="Jméno"
                        value={form.values.displayName}
                        onChange={(value) => form.setValue('displayName', value)}
                        error={form.fieldErrors.displayName}
                        helpText="Jak se člen zobrazuje v přehledech skupiny."
                        required
                        autoFocus
                    />
                    <SelectField
                        label="Uživatelský účet"
                        value={form.values.userId ?? BEZ_UCTU}
                        onChange={(value) => form.setValue('userId', value)}
                        error={form.fieldErrors.userId}
                        options={userOptions}
                        helpText="Člen bez účtu se do aplikace nepřihlásí, ale lze za něj evidovat útraty. Účet jde přiřadit kdykoli později."
                    />
                    {isEdit && (
                        <CheckboxField
                            label="Aktivní člen"
                            checked={form.values.isActive}
                            onChange={(checked) => form.setValue('isActive', checked)}
                            helpText="Neaktivní člen zůstává v historii, ale nelze mu přidávat nové pohyby."
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={onClose} className="su-tappable">
                        Zrušit
                    </Button>
                    <SubmitButton submitting={form.submitting}>{isEdit ? 'Uložit' : 'Přidat'}</SubmitButton>
                </Modal.Footer>
            </form>
        </Modal>
    )
}
